import { useState, useRef } from 'react'
import { makePage } from '@/components/page'
import { ProductGroupCreateBody } from 'shared/types/api'
import { VenueBottomButton } from '@/components/bottom-button'
import { Spinner } from '@/components/spinner'
import { useVenueContext } from '@/services/venue'
import { useAppContext } from '@/pages/_app'
import { FetchError } from '@/utils/fetch-error'
import { useRouter } from 'next/router'
import { customAlphabet } from 'nanoid'
import { useChosenProduct } from '@/services/scroll-position'
import { take } from 'rxjs/operators'
import { ProductGroupForm, ProductGroupFormRef } from '@/components/product-group-form'
import { uiEvents } from '@/utils/event'
import { HeadPanel } from '@/components/head-panel'

export const PRODUCT_GROUP_POSITION_URL_KEY = 'pos'

export default makePage(
  {
    clientRenderOnly: {},
    meta: {
      subTitle: 'new:newCategory',
      noIndex: true,
    },
  },
  function AddProductGroupPage() {
    const { apiClient, t } = useAppContext()
    const venueContext = useVenueContext()!
    const { venueName, admin, menuRoute } = venueContext
    const router = useRouter()
    useChosenProduct(venueName)

    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)

    const productGroupFormRef = useRef<ProductGroupFormRef>(null)

    const position = Number.parseInt(
      new URL(location.href).searchParams.get(PRODUCT_GROUP_POSITION_URL_KEY) || '0',
      10,
    )

    if (!admin.isAdmin) {
      return null
    }

    const formId = 'newProductGroup'

    return (
      <div>
        <HeadPanel
          url={menuRoute.path + '?viewMode=adminProductGroups'}
          text={t('new:newCategory')}
        />
        <div className="px-5 py-4">
          <form
            id={formId}
            onSubmit={async (e) => {
              e.preventDefault()
              if (isLoading) {
                return
              }
              const formResult = await productGroupFormRef.current!.process()
              if (formResult.type === 'validationError') {
                return
              }
              setIsLoading(true)

              const nanoid = customAlphabet('abcdefghijklmnopqrstuvwxyz-', 10)
              const productCreateBody: ProductGroupCreateBody = {
                ...formResult.body,
                name: nanoid(),
                position,
              }
              uiEvents.next({
                type: 'ScrollPositionChosenGroup',
                productGroup: {
                  productGroupName: productCreateBody.name,
                },
              })

              const fetchResult = await apiClient.fetch(
                `/admin/venue/${venueName}/product-group/new`,
                {
                  method: 'POST',
                  body: JSON.stringify(productCreateBody),
                },
              )

              if (fetchResult instanceof FetchError) {
                setIsError(true)
                return
              }

              venueContext.refreshVenue()
              venueContext.venueUpdated$.pipe(take(1)).subscribe(() => {
                router.push('/venue/[venueName]', `/venue/${venueName}?viewMode=adminProductGroups`)
              })
            }}
          >
            <ProductGroupForm ref={productGroupFormRef} />
          </form>
        </div>

        <VenueBottomButton>
          <button
            className="flex items-center w-full h-12 text-lg justify-center rounded-lg bg-back dark:bg-backAccentDark dark:text-primaryAccentDark font-medium"
            type="submit"
            form={formId}
          >
            {(() => {
              if (isError) {
                return (
                  <span className="font-medium text-negative dark:text-negativeDark cursor-not-allowed">
                    {t('common:error')}
                  </span>
                )
              }
              if (isLoading) {
                return (
                  <div className="w-6 inline-block mx-auto">
                    <Spinner />
                  </div>
                )
              }

              return <span>{t('common:create')}</span>
            })()}
          </button>
        </VenueBottomButton>
      </div>
    )
  },
)
